import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Select } from 'antd';

const validCustomFieldTypes = new Set([
  'yes-no',
  'dropdown',
  'text',
  'dateTime',
  'attribute',
  'staticText',
  'gpsLocation',
  'table',
  'dynamicAttribute',
]);

const getCustomFieldsForSection = (acc, customField) => {
  if (!customField) return;
  const { hidden, fields: ourFields, name: sectionName } = customField;
  if (hidden) return;
  ourFields?.forEach?.((subField) => {
    const { selectedType, configProps: subFieldConfig = {} } = subField;
    if (!validCustomFieldTypes.has(selectedType)) return;
    if (selectedType === 'table') {
      const { columns: subFieldCols = [] } = subFieldConfig;
      subFieldCols?.forEach?.((subFieldCol) => {
        const colTitle = `${subFieldConfig.title} - ${subFieldCol.name}`;
        const label = `${sectionName} - ${colTitle}`;
        acc[`${subField.id}-${subFieldCol.key}`] = {
          label,
          title: colTitle,
        };
      });
      return;
    }
    const label = `${sectionName} - ${subFieldConfig.title}`;
    acc[subField.id] = {
      label,
      title: subFieldConfig.title,
    };
  });
};

const getCustomFieldOptionsForDivision = (customFields, divisionId) => {
  const { fields = [] } = customFields[divisionId] ?? {};
  return fields
    .filter((section) => section.id !== 'timecard')
    .reduce((acc, customField) => {
      getCustomFieldsForSection(acc, customField);
      return acc;
    }, {});
};

export default function TimeEntryGroupedColumnSelect({
  generalOptions = {},
  divisions = [],
  customFields = {},
  columns = [],
  onSelect,
  onDeselect,
  isCustom = false,
}) {
  const divisionMap = useSelector((state) => state.settings.divisions);

  const divisionCustomFieldOptions = useMemo(() => (
    divisions.reduce((acc, divisionId) => {
      acc[divisionId] = getCustomFieldOptionsForDivision(customFields, divisionId);
      return acc;
    }, {})
  ), [customFields, divisions]);

  const generalOptionKeys = useMemo(() => (
    Object.keys(generalOptions)
  ), [generalOptions]);

  const findDivisionForCustomFieldKey = useCallback((fieldKey) => (
    divisions.find((divId) => fieldKey in (divisionCustomFieldOptions[divId] ?? {}))
  ), [divisions, divisionCustomFieldOptions]);

  const selectedValues = useMemo(() => (
    columns.map((col) => col.key)
  ), [columns]);

  const orphanColumns = useMemo(() => (
    columns.filter((col) => {
      if (col.divisionId) {
        const divisionOptions = divisionCustomFieldOptions[col.divisionId] ?? {};
        return !(col.key in divisionOptions);
      }
      return !(col.key in generalOptions);
    })
  ), [columns, divisionCustomFieldOptions, generalOptions]);

  const handleSelect = (key) => {
    const divisionId = findDivisionForCustomFieldKey(key);
    if (divisionId) {
      const { title } = divisionCustomFieldOptions[divisionId]?.[key] ?? {};
      onSelect(key, title, divisionId);
      return;
    }
    onSelect(key);
  };

  return (
    <Select
      style={{ width: 500 }}
      mode={isCustom ? 'tags' : 'multiple'}
      tokenSeparators={[',']}
      placeholder="Enter column names"
      optionFilterProp="label"
      value={selectedValues}
      onSelect={handleSelect}
      onDeselect={onDeselect}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...isCustom ? { open: false } : {}}
    >
      {generalOptionKeys.length > 0 && (
        <Select.OptGroup label="General Columns">
          {generalOptionKeys.map((opt) => (
            <Select.Option value={opt} key={opt} label={generalOptions[opt]}>
              {generalOptions[opt]}
            </Select.Option>
          ))}
        </Select.OptGroup>
      )}
      {divisions.map((divisionId) => {
        const options = divisionCustomFieldOptions[divisionId] ?? {};
        const optionEntries = Object.entries(options);
        if (!optionEntries.length) return null;

        return (
          <Select.OptGroup
            key={divisionId}
            label={divisionMap[divisionId]?.name ?? divisionId}
          >
            {optionEntries.map(([key, { label }]) => (
              <Select.Option value={key} key={`${divisionId}-${key}`} label={label}>
                {label}
              </Select.Option>
            ))}
          </Select.OptGroup>
        );
      })}
      {orphanColumns.map((col) => (
        <Select.Option value={col.key} key={col.key} label={col.name}>
          {col.name}
        </Select.Option>
      ))}
    </Select>
  );
}

TimeEntryGroupedColumnSelect.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    divisionId: PropTypes.string,
  })),
  customFields: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  divisions: PropTypes.arrayOf(PropTypes.string),
  generalOptions: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isCustom: PropTypes.bool,
  onDeselect: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

TimeEntryGroupedColumnSelect.defaultProps = {
  columns: [],
  customFields: {},
  divisions: [],
  generalOptions: {},
  isCustom: false,
};
